import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  Button,
  Box,
  Typography,
  Paper,
  ButtonBase,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import HelpHint from "../parts/helpHint";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { TiersApi } from "../../services/api/tiers";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ControlBar from "../dashboard/controlBar"; // Use shared ControlBar for consistency

// Tier Item Component
function TierItem({ data, onClick, id }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 0.5,
        borderRadius: { xs: 0, sm: 1 }, // No radius on mobile for edge-to-edge feel
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'primary.main',
        },
      }}
    >
      <ButtonBase
        onClick={onClick}
        id={id}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'stretch',
          textAlign: 'left',
        }}
      >
        {/* Color Bar */}
        <Box
          sx={{
            width: { xs: 4, sm: 6 },
            backgroundColor: data.color,
            flexShrink: 0,
          }}
        />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 2.5 },
            minHeight: { xs: 60, sm: 70 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem' },
              lineHeight: 1.3,
              mb: data.inheritance?.length > 0 ? 0.5 : 0,
            }}
          >
            {data.nickname}
          </Typography>

          {data.inheritance && data.inheritance.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                opacity: 0.8,
              }}
            >
              {data.inheritance.join(' + ')}
            </Typography>
          )}
        </Box>
      </ButtonBase>
    </Paper>
  );
}

// Sortable Tier Item Component
function SortableTierItem({ data, id }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={0}
      sx={{
        mb: 0.5,
        borderRadius: { xs: 0, sm: 1 }, // No radius on mobile for edge-to-edge feel
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'primary.main',
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Color Bar */}
        <Box
          sx={{
            width: { xs: 4, sm: 6 },
            backgroundColor: data.color,
            flexShrink: 0,
          }}
        />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 2.5 },
            minHeight: { xs: 60, sm: 70 },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                lineHeight: 1.3,
                mb: data.inheritance?.length > 0 ? 0.5 : 0,
              }}
            >
              {data.nickname}
            </Typography>

            {data.inheritance && data.inheritance.length > 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  opacity: 0.8,
                }}
              >
                Includes: {data.inheritance.join(', ')}
              </Typography>
            )}
          </Box>

          <DragIndicatorIcon sx={{ color: 'text.secondary', opacity: 0.5 }} />
        </Box>
      </Box>
    </Paper>
  );
}

// Main Tiers Component
function Tiers(props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State management
  const userCacheKey = `tiers_${props.user?.sub || 'unknown'}`;
  const [tiers, setTiers] = useState(
    localStorage.getItem(userCacheKey)
      ? JSON.parse(localStorage.getItem(userCacheKey))
      : []
  );
  const [isLoading, setIsLoading] = useState(!localStorage.getItem(userCacheKey));
  const [isReordering, setIsReordering] = useState(false);
  const [sortButton, setSortButton] = useState("Sort");

  // Drag and drop handling
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setTiers((tiers) => {
        const oldIndex = tiers.findIndex((tier) => tier.id === active.id);
        const newIndex = tiers.findIndex((tier) => tier.id === over.id);
        return arrayMove(tiers, oldIndex, newIndex);
      });
    }
  };

  // Sort toggle
  const handleSort = () => {
    if (!isReordering) {
      setSortButton("Save Changes");
      setIsReordering(true);
    } else {
      setSortButton("Sort");
      setIsReordering(false);
      sendReorderedList();
    }
  };

  // Save reordered list
  const sendReorderedList = async () => {
    try {
      let token = props.token;
      if (typeof token === 'function') {
        token = await token();
      }
      if (!token || typeof token !== 'string' || token === 'undefined') {
        toast.error('No valid token found for tiers API call.');
        return;
      }
      toast.success("Sort saved");
    } catch (err) {
      toast.error(`Sort Request Error: ${err}`);
    }
  };

  // Tier item click handler
  const handleTierClick = (tier) => {
    if (!isReordering) {
      navigate(`/tiers/${tier.relId}`);
    }
  };

  // Data fetching
  useEffect(() => {
    let isMounted = true;

    const getTierListEndpoint = async () => {
      let token = props.token;
      if (typeof token === 'function') {
        token = await token();
      }
      if (!token || typeof token !== 'string' || token === 'undefined') {
        toast.error('No valid token found for tiers API call.');
        return;
      }

      const response = await TiersApi.getTiers(token);

      if (isMounted) {
        if (response.success) {
          const data = response.data;
          if (Array.isArray(data)) {
            const list = data
              .filter((item) => item.relId !== 0 && item.relId !== "0")
              .map((item) => ({
                id: item.relId,
                title: item.tierName,
                relId: item.relId,
                nickname: item.tierName,
                color: item.color,
                parentTier: item.parentTier,
              }));
            getInheritanceInit(list);
          } else {
            toast.warn('Tiers API returned unexpected data format.');
            setIsLoading(false);
          }
        } else {
          toast.warn(`Something went wrong: ${response.status || 'Unknown error'}`);
          setIsLoading(false);
        }
      }
    };

    const getInheritanceInit = (tiersData) => {
      const tiersWithInheritance = tiersData.map((tier) => {
        const inheritance = getChildTiers(tier.relId, tiersData);
        return { ...tier, inheritance };
      });
      localStorage.setItem(userCacheKey, JSON.stringify(tiersWithInheritance));
      setTiers(tiersWithInheritance);
      setIsLoading(false);
    };

    const getChildTiers = (parentId, allTiers) => {
      const children = allTiers.filter(tier => tier.parentTier === parentId);
      let allChildren = [];
      
      children.forEach(child => {
        allChildren.push(child.nickname);
        // Recursively get grandchildren
        const grandChildren = getChildTiers(child.relId, allTiers);
        allChildren = allChildren.concat(grandChildren);
      });
      
      return allChildren;
    };

    getTierListEndpoint();
    window.scrollTo(0, 0);

    return () => {
      isMounted = false;
    };
  }, [props.token, props.user?.sub]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Loading state (centered similar to other list pages)
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', width: isMobile ? '95vw' : '100%', mx: 'auto' }}>
        <CircularProgress size={isMobile ? 40 : 60} />
      </Box>
    );
  }

  // Main render
  return (
    <>
      {/* Control Bar wrapper for consistent spacing */}
      <Box sx={{ width: isMobile ? '95vw' : '100%', px: { xs: 1, sm: 2, md: 3 }, mx: 'auto', boxSizing: 'border-box' }}>
        <ControlBar
          Button1="Add Tier"
          ButtonLink="/tiers/add/"
          Button2={sortButton}
          Button2Function={handleSort}
          helpHint={
            <HelpHint
              title="Tiers"
              hints={[
                "Create a new tier with the Add Tier button",
                "Drag and drop to reorder tiers",
                "Click a tier to view subscribers and rewards",
              ]}
            />
          }
        />
      </Box>
      {/* Content container */}
      <Box sx={{ width: isMobile ? '95vw' : '100%', px: { xs: 0.5, sm: 2, md: 3 }, mx: 'auto', boxSizing: 'border-box', pb: { xs: 10, sm: 6, md: 4 } }}>
      {tiers.length > 0 ? (
        <Box mt={1}>
          {isReordering ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                Drag tiers to reorder them
              </Typography>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={tiers} strategy={verticalListSortingStrategy}>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {tiers.map((tier) => (
                      <SortableTierItem
                        key={tier.id}
                        id={tier.id}
                        data={tier}
                      />
                    ))}
                  </motion.div>
                </SortableContext>
              </DndContext>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {tiers.map((tier, index) => (
                <motion.div
                  key={tier.relId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.05,
                  }}
                >
                  <TierItem
                    data={tier}
                    onClick={() => handleTierClick(tier)}
                    id={`tier${index}`}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </Box>
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          py: { xs: 4, sm: 8 }, 
          backgroundColor: 'background.paper',
          borderRadius: { xs: 1, sm: 2 },
          border: '2px dashed',
          borderColor: 'divider',
          mt: 3,
          mx: { xs: 0.5, sm: 0 }
        }}>
          <Typography variant="h4" color="text.secondary" sx={{ mb: 2, fontWeight: 300 }}>
            No Tiers Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            Create tiers to organize your clients and set up automatic distribution groups.
          </Typography>
          <Button
            component={Link}
            to="/tiers/add/"
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            sx={{ px: 4, py: 1.5 }}
          >
            Create Your First Tier
          </Button>
        </Box>
      )}
      </Box>
    </>
  );
}

export default Tiers;
